import React, { useMemo, useState } from 'react';
import {
    MdBorderStyle,
    MdPeople,
    MdTrendingUp,
    MdAndroid,
    MdPersonOutline,
} from 'react-icons/md';
import { injectIntl } from 'react-intl';

import PropTypes from 'prop-types';
import {
    BarChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    LabelList,
    ComposedChart,
    Legend,
} from 'recharts';

import {
    Typography,
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import ChartCard from '~/components/ChartCard';
import LocaleMessage from '~/components/LocaleMessage';

import { ChatCardContainer } from '../../Dashboard/styles';
import {
    buildCleanAreaByHourOfDay,
    CLEANING_REPORT_CHART_PERIOD_MSG_IDS,
    getCleaningReportDayKey,
    getCleaningReportPeriodDaysCount,
    getCleaningReportTaskStartTime,
    getVisibleCleaningReportChartPeriodIndices,
    resolveCleaningReportChartPeriodIdx,
} from './cleaningReportUtils';

/**
 * Formata segundos em uma string de duração legível por humanos.
 * @param {number} seconds - Total de segundos.
 * @returns {string} Duração formatada (ex: "1h 30m").
 */
const formatDuration = seconds => {
    if (!seconds || seconds <= 0) return '0h 0m';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

/**
 * Formata um número como uma área em metros quadrados.
 * @param {number|string} value - Valor da área.
 * @returns {string} Área formatada ou placeholder.
 */
const formatM2 = value => {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return `${Number(value).toFixed(1)}m²`;
};

/**
 * Calcula métricas de ROI comparando a produtividade do robô com a humana.
 * @param {number} totalArea - Área total.
 * @param {number} totalCleanTimeSec - Tempo total.
 * @param {number} humanProd - Produtividade humana.
 * @returns {Object} Métricas.
 */
const calculateROIMetrics = (totalArea, totalCleanTimeSec, humanProd) => {
    const robotHoursNum = (totalCleanTimeSec || 0) / 3600;
    const humanArea = humanProd > 0 ? humanProd * robotHoursNum : 0;
    const gainPercent =
        humanArea > 0 ? ((totalArea - humanArea) / humanArea) * 100 : 0;
    const humansNeeded =
        humanProd > 0 && totalArea > 0 ? Math.ceil(totalArea / humanArea) : 0;
    return { humanArea, gainPercent, humansNeeded };
};

/**
 * Célula de grade para métricas de ROI (Versão padrão SaaS)
 */
const ROINewCard = ({
    title,
    icon: Icon,
    val,
    desc,
    valColor,
    bgColor,
    textColor,
}) => (
    <div className="col-md-4 col-12 mb-3">
        <div
            className="jr-card"
            style={{
                backgroundColor: bgColor || '#fff',
                borderRadius: 8,
                padding: '16px 20px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: 'none',
            }}
        >
            {title && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 12,
                    }}
                >
                    {Icon && (
                        <Icon
                            size={18}
                            style={{
                                marginRight: 8,
                                color: textColor || valColor || '#666',
                            }}
                        />
                    )}
                    <Typography
                        variant="body2"
                        style={{
                            fontWeight: 600,
                            color: textColor || valColor || '#555',
                        }}
                    >
                        {title}
                    </Typography>
                </div>
            )}
            <Typography
                variant="h5"
                style={{
                    fontWeight: 'bold',
                    marginBottom: 4,
                    color: textColor || valColor || '#333',
                }}
            >
                {val}
            </Typography>
            <Typography variant="body2" style={{ color: textColor || '#777' }}>
                {desc}
            </Typography>
        </div>
    </div>
);

/**
 * Gráfico de barras verticais para comparação (Novo).
 */
const ROICardOverviewChart = injectIntl(
    ({ totalArea, humanArea, durationStr, intl, cardColor }) => {
        return (
            <Box
                mt={1}
                p={3}
                className="jr-card"
                style={{
                    backgroundColor: '#fff',
                    borderRadius: 12,
                    border: 'none',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 20,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        style={{ fontWeight: 600, color: '#444' }}
                    >
                        <LocaleMessage msg="page.robots.cleaning_report.roi.area_compare_title" />
                    </Typography>
                    <Typography variant="body2" style={{ color: '#888' }}>
                        <LocaleMessage
                            msg="page.robots.cleaning_report.roi.robot_operation_time"
                            values={{ time: durationStr }}
                        />
                    </Typography>
                </div>
                <ResponsiveContainer width="100%" height={90}>
                    <BarChart
                        layout="vertical"
                        data={[
                            {
                                name: intl.formatMessage({
                                    id: 'page.robots.cleaning_report.roi.robot',
                                }),
                                area: Number(totalArea.toFixed(1)),
                                fill: cardColor || '#2152a5', // Cor do robô baseada no tema
                            },
                            {
                                name: intl.formatMessage({
                                    id: 'page.robots.cleaning_report.roi.human',
                                }),
                                area: Number(humanArea.toFixed(1)),
                                fill: '#9e9e9e', // Cor cinza do operador
                            },
                        ]}
                        margin={{ top: 0, right: 80, left: 0, bottom: 0 }}
                    >
                        <XAxis type="number" hide domain={[0, 'dataMax']} />
                        <YAxis
                            type="category"
                            dataKey="name"
                            axisLine={false}
                            tickLine={false}
                            width={110}
                            tick={props => {
                                const { x, y, payload } = props;
                                const isRobot = payload.value
                                    .toLowerCase()
                                    .includes('rob');
                                const Icon = isRobot
                                    ? MdAndroid
                                    : MdPersonOutline;
                                return (
                                    <foreignObject
                                        x={x - 110}
                                        y={y - 12}
                                        width={100}
                                        height={24}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                height: '100%',
                                            }}
                                        >
                                            <Icon
                                                size={16}
                                                style={{
                                                    marginRight: 6,
                                                    color: '#666',
                                                }}
                                            />
                                            <span
                                                style={{
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                    color: '#444',
                                                }}
                                            >
                                                {payload.value}
                                            </span>
                                        </div>
                                    </foreignObject>
                                );
                            }}
                        />
                        <RechartsTooltip
                            formatter={v => `${v} m²`}
                            cursor={{ fill: 'transparent' }}
                        />
                        <Bar dataKey="area" barSize={24} radius={4}>
                            <LabelList
                                dataKey="area"
                                position="right"
                                fill="#333"
                                formatter={v => `${v} m²`}
                                style={{ fontWeight: 600 }}
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        );
    }
);

/**
 * Caixa de texto de resumo
 */
const ROISummaryBox = ({
    humansNeeded,
    totalArea,
    durationStr,
    multiplier,
}) => (
    <Box mt={3}>
        <Alert severity="success" variant="standard">
            <Typography
                variant="body2"
                style={{ fontWeight: 500, lineHeight: 1.5 }}
            >
                <LocaleMessage
                    msg="page.robots.cleaning_report.roi.summary_text"
                    values={{
                        humans: humansNeeded,
                        area: formatM2(totalArea),
                        time: durationStr,
                        multiplier,
                    }}
                />
            </Typography>
        </Alert>
    </Box>
);

/**
 * Transformação de dados do gráfico diário.
 */
const transformDailyData = (cleaningReports, daysCount, humanProd) => {
    const hourlyReal = buildCleanAreaByHourOfDay(cleaningReports);

    const hourlyTime = new Array(24).fill(0);
    cleaningReports.forEach(item => {
        const t = getCleaningReportTaskStartTime(item);
        if (!t) return;

        let h = null;
        if (typeof t === 'number') {
            const ms = t > 1e12 ? t : t * 1000;
            const d = new Date(ms);
            if (!Number.isNaN(d.getTime())) h = d.getHours();
        } else {
            const s = String(t).trim();
            if (s.includes('T')) {
                const m = /^(\d{2})/.exec((s.split('T')[1] || '').trim());
                if (m) h = parseInt(m[1], 10);
            } else {
                const spaceIdx = s.search(/\s/);
                if (spaceIdx >= 0) {
                    const m = /^(\d{2})/.exec(s.slice(spaceIdx + 1).trim());
                    if (m) h = parseInt(m[1], 10);
                }
            }
        }

        if (h != null && h >= 0 && h <= 23) {
            hourlyTime[h] += Number(item.clean_time) || 0;
        }
    });

    return hourlyReal.map((h, index) => ({
        name: h.label,
        robot: Number(h.clean_area.toFixed(0)),
        human: Number(
            (((hourlyTime[index] || 0) / 3600) * humanProd).toFixed(0)
        ),
    }));
};

/**
 * Transformação de dados do gráfico semanal.
 */
const transformWeeklyData = (cleaningReports, daysCount, humanProd, labels) => {
    const dowArea = new Array(7).fill(0);
    const dowTime = new Array(7).fill(0);
    cleaningReports.forEach(item => {
        const dayKey = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(item)
        );
        if (dayKey !== '—') {
            const [y, m, d] = dayKey.split('-');
            const dObj = new Date(Number(y), Number(m) - 1, Number(d));
            dowArea[dObj.getDay()] += Number(item.clean_area) || 0;
            dowTime[dObj.getDay()] += Number(item.clean_time) || 0;
        }
    });
    return labels.map((label, i) => ({
        name: label,
        robot: Number(dowArea[i].toFixed(0)),
        human: Number((((dowTime[i] || 0) / 3600) * humanProd).toFixed(0)),
    }));
};

/**
 * Transformação de dados do gráfico num formato de linha do tempo (Timeline) para o período selecionado.
 */
const transformMonthlyData = (
    cleaningReports,
    daysCount,
    humanProd,
    labels,
    periodDates,
    intl
) => {
    const localeFormat = intl.locale || 'pt-BR';
    const byDayArea = {};
    const byDayTime = {};

    // Preenche todos os dias do período com 0 para garantir continuidade
    if (periodDates && periodDates.startDate && periodDates.endDate) {
        const parseDateString = dStr => {
            const parts = dStr.split('T')[0].split('-');
            return new Date(
                Number(parts[0]),
                Number(parts[1]) - 1,
                Number(parts[2])
            );
        };
        const current = parseDateString(periodDates.startDate);
        const end = parseDateString(periodDates.endDate);
        let safety = 0;
        while (current <= end && safety < 1000) {
            const y = current.getFullYear();
            const m = String(current.getMonth() + 1).padStart(2, '0');
            const day = String(current.getDate()).padStart(2, '0');
            byDayArea[`${y}-${m}-${day}`] = 0;
            byDayTime[`${y}-${m}-${day}`] = 0;
            current.setDate(current.getDate() + 1);
            safety += 1;
        }
    }

    cleaningReports.forEach(item => {
        const dayKey = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(item)
        );
        if (dayKey !== '—') {
            byDayArea[dayKey] =
                (byDayArea[dayKey] || 0) + (Number(item.clean_area) || 0);
            byDayTime[dayKey] =
                (byDayTime[dayKey] || 0) + (Number(item.clean_time) || 0);
        }
    });

    const sortedDays = Object.keys(byDayArea).sort();
    return sortedDays.map(key => {
        const [y, m, d] = key.split('-');
        const dateObj = new Date(Number(y), Number(m) - 1, Number(d));
        return {
            name: dateObj.toLocaleDateString(localeFormat, {
                day: '2-digit',
                month: '2-digit',
            }),
            robot: Number(byDayArea[key].toFixed(0)),
            human: Number(
                (((byDayTime[key] || 0) / 3600) * humanProd).toFixed(0)
            ),
        };
    });
};

/**
 * Transformação de dados do gráfico anual.
 */
const transformAnnualData = (cleaningReports, daysCount, humanProd, labels) => {
    const monthArea = new Array(12).fill(0);
    const monthTime = new Array(12).fill(0);
    cleaningReports.forEach(item => {
        const dayKey = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(item)
        );
        if (dayKey !== '—') {
            const m = Number(dayKey.split('-')[1]);
            monthArea[m - 1] += Number(item.clean_area) || 0;
            monthTime[m - 1] += Number(item.clean_time) || 0;
        }
    });
    return labels.map((label, i) => ({
        name: label,
        robot: Number(monthArea[i].toFixed(0)),
        human: Number((((monthTime[i] || 0) / 3600) * humanProd).toFixed(0)),
    }));
};

/**
 * Componente principal do Card de ROI.
 */
function CleaningReportROICard({
    totalArea: totalAreaProp,
    humanProd: humanProdProp,
    cardColor,
    list: cleaningReports,
    periodDates,
    intl,
}) {
    const [selectedPeriodIdx, setSelectedPeriodIdx] = useState(null);

    const totalArea = parseFloat(totalAreaProp) || 0;
    const humanProd =
        humanProdProp != null && humanProdProp !== ''
            ? parseFloat(humanProdProp)
            : 150;
    const totalCleanTimeSec = useMemo(
        () =>
            (cleaningReports || []).reduce(
                (acc, curr) => acc + (Number(curr.clean_time) || 0),
                0
            ),
        [cleaningReports]
    );

    const PERIODS = useMemo(
        () =>
            CLEANING_REPORT_CHART_PERIOD_MSG_IDS.map(id =>
                intl.formatMessage({ id })
            ),
        [intl]
    );

    const LABELS_MAP = useMemo(
        () => ({
            [PERIODS[0]]: Array.from({ length: 24 }, (_, i) => `${i}h`),
            [PERIODS[1]]: [
                intl.formatMessage({ id: 'label.day.sun' }),
                intl.formatMessage({ id: 'label.day.mon' }),
                intl.formatMessage({ id: 'label.day.tue' }),
                intl.formatMessage({ id: 'label.day.wed' }),
                intl.formatMessage({ id: 'label.day.thu' }),
                intl.formatMessage({ id: 'label.day.fri' }),
                intl.formatMessage({ id: 'label.day.sat' }),
            ],
            [PERIODS[3]]: [
                intl.formatMessage({ id: 'label.month.jan' }),
                intl.formatMessage({ id: 'label.month.feb' }),
                intl.formatMessage({ id: 'label.month.mar' }),
                intl.formatMessage({ id: 'label.month.apr' }),
                intl.formatMessage({ id: 'label.month.may' }),
                intl.formatMessage({ id: 'label.month.jun' }),
                intl.formatMessage({ id: 'label.month.jul' }),
                intl.formatMessage({ id: 'label.month.aug' }),
                intl.formatMessage({ id: 'label.month.sep' }),
                intl.formatMessage({ id: 'label.month.oct' }),
                intl.formatMessage({ id: 'label.month.nov' }),
                intl.formatMessage({ id: 'label.month.dec' }),
            ],
        }),
        [PERIODS, intl]
    );

    const metrics = useMemo(
        () => calculateROIMetrics(totalArea, totalCleanTimeSec, humanProd),
        [totalArea, totalCleanTimeSec, humanProd]
    );

    const daysCount = useMemo(
        () => getCleaningReportPeriodDaysCount(periodDates),
        [periodDates]
    );

    const visiblePeriodIndices = useMemo(
        () =>
            getVisibleCleaningReportChartPeriodIndices(periodDates, daysCount),
        [periodDates, daysCount]
    );

    const periodIdx = useMemo(
        () =>
            resolveCleaningReportChartPeriodIdx(
                selectedPeriodIdx,
                periodDates,
                daysCount
            ),
        [selectedPeriodIdx, periodDates, daysCount]
    );

    const chartData = useMemo(() => {
        const labels = LABELS_MAP[PERIODS[periodIdx]] || LABELS_MAP[PERIODS[0]];
        if (periodIdx === 0)
            return transformDailyData(cleaningReports, daysCount, humanProd);
        if (periodIdx === 1)
            return transformWeeklyData(
                cleaningReports,
                daysCount,
                humanProd,
                labels
            );
        if (periodIdx === 2)
            return transformMonthlyData(
                cleaningReports,
                daysCount,
                humanProd,
                null,
                periodDates,
                intl
            );
        return transformAnnualData(
            cleaningReports,
            daysCount,
            humanProd,
            LABELS_MAP[PERIODS[3]]
        );
    }, [
        periodIdx,
        cleaningReports,
        daysCount,
        humanProd,
        PERIODS,
        LABELS_MAP,
        periodDates,
        intl,
    ]);

    return (
        <div className="row card-row">
            <div className="col-12 mb-5 dash-card">
                <ChatCardContainer>
                    <ChartCard
                        className="animated slideInFromRight animation-duration-3"
                        title={
                            <LocaleMessage msg="page.robots.cleaning_report.roi.title" />
                        }
                        desc={
                            <LocaleMessage msg="page.robots.cleaning_report.roi.description" />
                        }
                        value=""
                    >
                        <div className="p-3">
                            <div className="row">
                                <ROINewCard
                                    title={
                                        <LocaleMessage msg="page.robots.cleaning_report.roi.clean_area_title" />
                                    }
                                    icon={MdBorderStyle}
                                    val={formatM2(totalArea)}
                                    desc={
                                        <LocaleMessage msg="page.robots.cleaning_report.roi.clean_area_desc" />
                                    }
                                    valColor={cardColor}
                                />
                                <ROINewCard
                                    title={
                                        <LocaleMessage msg="page.robots.cleaning_report.roi.team_title" />
                                    }
                                    icon={MdPeople}
                                    val={
                                        <LocaleMessage
                                            msg="page.robots.cleaning_report.roi.team_count"
                                            values={{
                                                count: metrics.humansNeeded,
                                            }}
                                        />
                                    }
                                    desc={
                                        <LocaleMessage msg="page.robots.cleaning_report.roi.team_desc" />
                                    }
                                    valColor={cardColor}
                                />
                                <ROINewCard
                                    title={
                                        <LocaleMessage msg="page.robots.cleaning_report.roi.productivity_title" />
                                    }
                                    icon={MdTrendingUp}
                                    val={
                                        <LocaleMessage
                                            msg="page.robots.cleaning_report.roi.productivity_count"
                                            values={{
                                                multiplier:
                                                    metrics.humanArea > 0
                                                        ? (
                                                              totalArea /
                                                              metrics.humanArea
                                                          ).toFixed(1)
                                                        : 0,
                                            }}
                                        />
                                    }
                                    desc={
                                        <LocaleMessage msg="page.robots.cleaning_report.roi.productivity_desc" />
                                    }
                                    valColor={cardColor}
                                    bgColor={cardColor}
                                    textColor="#fff"
                                />
                                <div className="col-12">
                                    <ROICardOverviewChart
                                        totalArea={totalArea}
                                        humanArea={metrics.humanArea}
                                        durationStr={formatDuration(
                                            totalCleanTimeSec
                                        )}
                                        cardColor={cardColor}
                                    />
                                </div>

                                <div className="col-12">
                                    <ROISummaryBox
                                        humansNeeded={metrics.humansNeeded}
                                        totalArea={totalArea}
                                        durationStr={formatDuration(
                                            totalCleanTimeSec
                                        )}
                                        multiplier={
                                            metrics.humanArea > 0
                                                ? (
                                                      totalArea /
                                                      metrics.humanArea
                                                  ).toFixed(1)
                                                : 0
                                        }
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            className="row p-3 align-items-center"
                            style={{ borderTop: '1px solid #eee' }}
                        >
                            <div className="col-12 mb-3 d-flex justify-content-end">
                                <FormControl
                                    size="small"
                                    style={{ minWidth: 200 }}
                                >
                                    <InputLabel>
                                        <LocaleMessage msg="page.robots.cleaning_report.group_comparison.metric_label" />{' '}
                                    </InputLabel>
                                    <Select
                                        value={periodIdx}
                                        onChange={e =>
                                            setSelectedPeriodIdx(
                                                Number(e.target.value)
                                            )
                                        }
                                    >
                                        {visiblePeriodIndices.map(idx => (
                                            <MenuItem key={idx} value={idx}>
                                                {PERIODS[idx]}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                            <div className="col-12">
                                <ResponsiveContainer width="100%" height={250}>
                                    <ComposedChart
                                        data={chartData}
                                        margin={{
                                            top: 10,
                                            right: 10,
                                            bottom: 0,
                                            left: 10,
                                        }}
                                    >
                                        <CartesianGrid
                                            stroke="#f5f5f5"
                                            strokeDasharray="3 3"
                                        />
                                        <XAxis
                                            dataKey="name"
                                            fontSize={12}
                                            padding={{ left: 0, right: 0 }}
                                        />
                                        <YAxis
                                            yAxisId="left"
                                            fontSize={12}
                                            tickFormatter={v => `${v}m²`}
                                        />
                                        <RechartsTooltip />
                                        <Legend
                                            wrapperStyle={{ fontSize: 12 }}
                                        />
                                        <Line
                                            type="monotone"
                                            yAxisId="left"
                                            dataKey="human"
                                            stroke="#b0bec5"
                                            strokeWidth={3}
                                            dot={{ r: 4 }}
                                            activeDot={{ r: 6 }}
                                            name={intl.formatMessage({
                                                id:
                                                    'page.robots.cleaning_report.roi.human_area_unit',
                                            })}
                                        />
                                        <Line
                                            type="monotone"
                                            yAxisId="left"
                                            dataKey="robot"
                                            stroke={cardColor}
                                            strokeWidth={3}
                                            dot={{ r: 4 }}
                                            activeDot={{ r: 6 }}
                                            name={intl.formatMessage({
                                                id:
                                                    'page.robots.cleaning_report.roi.robot_area_unit',
                                            })}
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </ChartCard>
                </ChatCardContainer>
            </div>
        </div>
    );
}

CleaningReportROICard.propTypes = {
    totalArea: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    humanProd: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    list: PropTypes.arrayOf(
        PropTypes.shape({
            start_time: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
            end_time: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            clean_area: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
        })
    ),
    periodDates: PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
    }),
};

CleaningReportROICard.defaultProps = {
    list: [],
    humanProd: 150,
    periodDates: null,
};

export default injectIntl(CleaningReportROICard);
